// src/components/desk/ManualClientModal.jsx — the manual "Add client" modal and the shared
// criteria form under it: PolygonMapPicker (search-area map), TierTagPicker + the criteria
// chip vocabulary (APT_CLASS/PROP_STATE/PROJECT_TYPES/dealbreakers), featureDefOf/InfoDot/
// FeatureDefPanel, CriteriaFieldsForm (also used by the requirement edit modal, which is why
// it is exported), the intent/category/urgency option lists, the envelope⇄review adapters
// (extractRequirementCmd/amendRequirementCmd), ManualClientModal itself and FillLinkSection —
// extracted VERBATIM from IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition,
// behavior-preserving). Everything IntakeTab (or a sibling desk module) still references is
// exported and imported back there.

import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import { t, tLog, getLanguage } from '../../lib/i18n.js'
import { Icon, IconText } from '../Icon.jsx'
import { card, input, btn, COUNTER, Modal, pill } from './primitives.jsx'
import { BetterToKnow, clientGaps, TIER_ICON, TIER_STYLE } from './MatchSuggestions.jsx'
import { MultiSelect } from '../AgentsTab.jsx'
import { checkPhone, splitDial } from '../../lib/phoneRules.js'
import { intentLabel } from '../../lib/labels.js'
import { commitIntakeEnvelope, createFillLink, findPartyCandidates, getComplexesByIds, listFillLinks, parseIntakeEnvelope, searchComplexes } from '../../db/core.js'
import { splitClientParse, acceptedClientPatch } from '../../lib/clientParseFields.js'
import { ParseConflictPanel } from './ParseConflictPanel.jsx'
import { getLocations } from '../../db/locations.js'
import { DateInput } from '../SegmentedInput.jsx'
import { LOC_RADIUS_BY_TYPE, NEAR_SEARCH_RADIUS_M } from '../../lib/deskFeed.js'

function extractRequirementCmd(envelope) {
  return (envelope?.commands || []).find(c => c.type === 'extract_requirement') || null
}
function amendRequirementCmd(envelope) {
  return (envelope?.commands || []).find(c => c.type === 'amend_requirement') || null
}

// ─── Add client — manual entry, no AI involved ─────────────────────────────────
// Distinct from both ClientReview (reviews an AI draft) and ClientEditModal (edits an
// already-saved demand): this is the plain "I'm typing this in myself" path — an agent on
// the phone with a client who never sent a pasteable message. Still goes through the same
// execute_proposal gateway as everything else (extract_requirement, human_edited: true) —
// the backend doesn't get a separate "manual" code path, only the frontend does.
// buy and rent are DIFFERENT requests (owner decision 2026-07-10, reaffirmed + actually
// enforced 2026-07-19): a client wanting both gets TWO requirements — add the client twice,
// the party-reuse picker (0123/0124) links them to one person — never one blended form.
// The blended 'buy_or_rent' option is NOT offered here. It stays supported downstream
// (scorer INTENT_MAP maps it to [sale, rent]; the DB check + person-view label keep it)
// ONLY for the 2 legacy rows that predate this enforcement — nothing new can create one.
// girao (0219): a client looking to PUT UP a gira sum rather than buy or rent. Same word
// on both sides of the market — Georgian has no separate verb for wanting one vs offering
// one, the way ყიდვა/გაყიდვა splits buy from sell. Supply-side girao shipped first; without
// this option the DB accepted a girao requirement that no screen could create.
const INTENT_OPTIONS = ['buy', 'rent', 'short_term_rent', 'girao']
const PROPERTY_CATEGORY_OPTIONS = ['apartment', 'room', 'house', 'land', 'commercial', 'cottage', 'office', 'garage', 'hotel', 'other']
const URGENCY_OPTIONS = ['immediate', 'soon', 'flexible', 'browsing']
// Values above stay English (stored + matched against as data); only the <option> display
// text is translated, via these value -> i18n-key lookups.
// (INTENT_OPTION_KEYS retired 2026-07-23 → src/lib/labels.js intentLabel(v,'demand'))
const PROPERTY_CATEGORY_KEYS = { apartment: 'desk_category_opt_apartment', room: 'desk_category_opt_room', house: 'desk_category_opt_house', land: 'desk_category_opt_land', commercial: 'desk_category_opt_commercial', cottage: 'desk_category_opt_cottage', office: 'desk_category_opt_office', garage: 'desk_category_opt_garage', hotel: 'desk_category_opt_hotel', other: 'desk_category_opt_other' }
const URGENCY_OPTION_KEYS = { immediate: 'desk_urgency_opt_immediate', soon: 'desk_urgency_opt_soon', flexible: 'desk_urgency_opt_flexible', browsing: 'desk_urgency_opt_browsing' }

// ─── Search-area map — vanilla Leaflet (no react-leaflet/leaflet-draw installed; npm
// install is unsafe in this Drive-synced folder, see memory), same L.map/tileLayer pattern
// as StreetsTab.jsx. Two ways to add vertices, both append to the same polygon so they can
// be mixed: a plain click drops one precise vertex (good for matching a corner); a
// click-and-drag sketches a run of vertices along the cursor path, throttled to one point
// per ~8px moved (good for a quick rough outline, or tracing a curved street). A Draw/Pan
// toggle exists because dragging can't mean both "draw" and "move the map" at once — Pan
// mode hands the gesture back to Leaflet's normal panning, Draw disables it so a drag never
// accidentally scrolls the map instead of sketching. value/onChange carry GeoJSON Polygon |
// null — the exact shape execute_proposal's search_polygon handling (migration 0041) expects.
function PolygonMapPicker({ value, onChange }) {
  const mapEl = useRef(null), mapRef = useRef(null), layerRef = useRef(null)
  const initedRef = useRef(false)
  const [points, setPoints] = useState([])   // [[lat,lng], ...]
  const [mode, setMode] = useState('draw')   // 'draw' | 'pan'
  const modeRef = useRef(mode)
  useEffect(() => { modeRef.current = mode }, [mode])

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return
    // doubleClickZoom: false — Leaflet's default treats two fast clicks as one 'dblclick'
    // (zoom in), swallowing both as vertex clicks. Two quick corner-clicks while drawing
    // would otherwise silently zoom the map instead of placing points (found while testing).
    const map = L.map(mapEl.current, { doubleClickZoom: false }).setView([41.715, 44.79], 12)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(map)

    const drag = { active: false, moved: false, lastPx: null }
    map.on('mousedown', e => {
      if (modeRef.current !== 'draw') return
      drag.active = true; drag.moved = false; drag.lastPx = map.latLngToContainerPoint(e.latlng)
      map.dragging.disable()
    })
    map.on('mousemove', e => {
      if (!drag.active) return
      const px = map.latLngToContainerPoint(e.latlng)
      if (px.distanceTo(drag.lastPx) < 8) return   // throttle: a new point every ~8px moved
      drag.moved = true; drag.lastPx = px
      setPoints(pts => [...pts, [e.latlng.lat, e.latlng.lng]])
    })
    map.on('mouseup', e => {
      if (!drag.active) return
      map.dragging.enable()
      if (!drag.moved) setPoints(pts => [...pts, [e.latlng.lat, e.latlng.lng]])   // plain click → one vertex
      drag.active = false
    })
    mapRef.current = map
    return () => { map.remove(); mapRef.current = null }
  }, [])

  // Cursor reflects mode — set imperatively (map.getContainer(), not React's className) because
  // Leaflet appends its OWN classes (leaflet-container, leaflet-touch, …) straight onto that
  // div outside React's knowledge; if React's className prop ever re-renders that node, it
  // overwrites the whole attribute and wipes Leaflet's classes with it, breaking the map's own
  // CSS (found live: switching Draw→Pan blanked the tile layer — the container lost
  // "leaflet-container" the moment `mode` touched its className template string).
  useEffect(() => {
    mapRef.current?.getContainer().style.setProperty('cursor', mode === 'draw' ? 'crosshair' : '')
  }, [mode])

  // Load an existing polygon once (async prop arrival from getRequirement) — never re-syncs
  // after that, so it doesn't fight the human's own edits on every parent re-render.
  useEffect(() => {
    if (initedRef.current || !value?.coordinates?.[0]) return
    initedRef.current = true
    setPoints(value.coordinates[0].slice(0, -1).map(([lng, lat]) => [lat, lng]))
  }, [value])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null }
    if (points.length >= 3) {
      layerRef.current = L.polygon(points, { color: '#f59e0b', fillOpacity: 0.15 }).addTo(map)
      const ring = [...points, points[0]].map(([lat, lng]) => [lng, lat])
      onChange({ type: 'Polygon', coordinates: [ring] })
    } else if (points.length > 0) {
      layerRef.current = L.polyline(points, { color: '#f59e0b' }).addTo(map)
      onChange(null)
    } else {
      onChange(null)
    }
  }, [points])

  return (
    <div>
      {/* isolate: Leaflet's own CSS puts its panes/controls at z-index up to 1000 (e.g.
          .leaflet-top/.leaflet-bottom = 1000); without a stacking context here those escape
          and render on top of the FEATURES dropdowns above (z-50), even though the dropdown
          is later in the DOM. isolate contains Leaflet's z-indices inside this box. */}
      <div ref={mapEl} className="isolate h-56 rounded-lg overflow-hidden border border-border" />
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
          <button type="button" onClick={() => setMode('draw')}
            className={`px-2.5 py-1 rounded-md text-xs transition-all ${mode === 'draw' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {t('desk_map_draw')}
          </button>
          <button type="button" onClick={() => setMode('pan')}
            className={`px-2.5 py-1 rounded-md text-xs transition-all ${mode === 'pan' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {t('desk_map_move')}
          </button>
        </div>
        <button type="button" className={btn('ghost')} disabled={!points.length} onClick={() => setPoints(p => p.slice(0, -1))}><IconText>{t('desk_map_undo_point')}</IconText></button>
        <button type="button" className={btn('ghost')} disabled={!points.length} onClick={() => setPoints([])}>{t('desk_map_clear_area')}</button>
        <span className="text-xs text-muted-foreground">
          {points.length === 0 ? (mode === 'draw' ? t('desk_map_draw_hint_empty') : t('desk_map_pan_hint')) : points.length < 3 ? tLog('desk_map_points_progress', { n: points.length }) : tLog('desk_map_area_set', { n: points.length })}
        </span>
      </div>
    </div>
  )
}

// ─── Shared criteria fields — price/size/districts/features/search-area — used by both
// the manual "Add client" form and the edit-existing modal. Not the AI-review card
// (ClientReview above): those show read-only badges pre-commit, these are live inputs. ───
// ─── Tier chips: one searchable picker over a big vocab, three-state selection ──
// Clients don't fill three separate dropdowns; the agent types a few letters, taps the
// item, and taps the chip again to cycle its meaning: ✓ must → ★ nice → ✗ not → gone.
// (defaultTier picks what the FIRST tap means — 'must' for BOTH districts and
// dealbreakers since 2026-07-20; districts previously defaulted to 'nice', which
// silently recorded every stated location as a non-binding nudge — see the picker
// call site.) Districts group by city so the whole-Georgia list stays navigable.
// TIER_STYLE / TIER_ICON — moved to ./desk/MatchSuggestions.jsx (plan §3 decomposition).
// A chip whose state is chosen from a MENU, not advanced by repeated taps (owner ruling
// 2026-07-24: "add this dropdown option in filling the form instead of clicking through to
// change states"). Same grammar as the policy-stance popover on match cards, so both sides
// of the app answer "what does this chip mean?" the same way.
//
// ⚠️ This is not only ergonomics. The type/dealbreaker chips cycled null → must → not →
// null, so `nice` (★ სასურველი) was UNREACHABLE from the form: three tiers exist and only
// two could ever be set. Every tier is now one tap away, and — the real win — a tap can no
// longer overshoot, which on a cycle meant going all the way round to correct it.
function TierMenu({ code, label, tier, setTier, tiers = ['must', 'nice', 'not'], children }) {
  const [open, setOpen] = useState(false)
  // Close on any outside click, capture phase — the chip sits inside a modal whose own
  // handlers stop propagation (same reason ContactPill uses capture).
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = e => { if (!ref.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('click', onDoc, true)
    return () => document.removeEventListener('click', onDoc, true)
  }, [open])
  return (
    <span className="relative inline-flex" ref={ref}>
      <button type="button" title={t('desk_tier_chip_cycle_hint')}
        onClick={() => setOpen(o => !o)}
        className={pill('none', { door: true, extra: tier ? TIER_STYLE[tier] : 'border-border text-muted-foreground hover:bg-muted' })}>
        {tier ? `${TIER_ICON[tier]} ` : ''}{label}{children}
      </button>
      {open && (
        <span className="absolute z-20 top-full left-0 mt-1 flex flex-col gap-1 p-1.5 rounded-md border border-border bg-popover shadow-lg w-max">
          {tiers.map(tr => (
            <button key={tr} type="button" onClick={() => { setTier(code, tr); setOpen(false) }}
              className={pill('none', { door: true, extra: `text-left ${TIER_STYLE[tr]}` })}>
              {TIER_ICON[tr]} {t(`desk_tier_${tr}`)}
            </button>
          ))}
          {/* Remove is only offered once there IS something to remove — an "off" option on
              an unset chip is a no-op that reads like a fourth state. */}
          {tier && (
            <button type="button" onClick={() => { setTier(code, null); setOpen(false) }}
              className={pill('neutral', { door: true, extra: 'text-left text-muted-foreground hover:bg-muted' })}>
              ✕ {t('desk_tier_clear')}
            </button>
          )}
        </span>
      )}
    </span>
  )
}

function TierTagPicker({ options, tierOf, setTier, tiers = ['must', 'nice', 'not'], defaultTier = 'nice', placeholder }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  // Picking a search result ADDS at defaultTier; the chip's own menu (TierMenu) is where a
  // tier is changed. Two separate gestures — before this, one tap did both, so tapping a
  // result you had already chosen silently advanced its tier instead of confirming it.
  // ⚠️ An already-set code keeps its tier: re-picking must never stomp a deliberate choice.
  const pick = code => { if (tierOf(code) === null) setTier(code, defaultTier) }
  const selected = options.filter(o => tierOf(o.code) !== null)
  const ql = q.trim().toLowerCase()
  // `search` carries every language's name (ka+en+ru) — an agent types "vake" or "ვაკე"
  // and must hit the same district regardless of which name the label shows.
  const results = ql ? options.filter(o => (o.search || o.label).toLowerCase().includes(ql) || (o.group || '').toLowerCase().includes(ql)).slice(0, 30) : []
  let lastGroup = null
  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map(o => (
            <TierMenu key={o.code} code={o.code} label={o.label} tier={tierOf(o.code)}
              setTier={setTier} tiers={tiers} />
          ))}
        </div>
      )}
      <input className={input} value={q} placeholder={placeholder}
        onChange={e => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && results.length > 0 && (
        <div className="max-h-48 overflow-y-auto border border-border rounded-md mt-1 bg-background">
          {results.map(o => {
            const header = o.group && o.group !== lastGroup ? o.group : null
            lastGroup = o.group
            const tier = tierOf(o.code)
            return (
              <div key={o.code}>
                {header && <p className="px-2 pt-1.5 pb-0.5 text-xs uppercase text-muted-foreground">{header}</p>}
                <button type="button" className="w-full text-left px-2 py-1 text-sm hover:bg-muted flex justify-between items-center"
                  onMouseDown={e => { e.preventDefault(); pick(o.code); setQ('') }}>
                  <span>{o.label}</span>
                  {tier && <span className={`${COUNTER} border ${TIER_STYLE[tier]}`}>{TIER_ICON[tier]}</span>}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// The quality ladders (validated against the Tbilisi market 2026-07-10): clients think
// in composite LEVELS ("სუფთა ბინა", "პრემიუმ კლასის"), not renovation jargon or item
// lists. Renters get the 8-level apartment_class ladder (migration 0078 — the SAME codes
// the property modal assigns, so demand and supply actually match); buyers think in
// FRAMES for unfinished stock plus the class ladder for move-in-ready. One level + a
// couple of dealbreaker chips replaces itemized feature shopping. The granular
// renovation codes (reno_*) stay PROPERTY-side facts, deliberately not offered here.
// SYMMETRY RULE: these are the SAME feature codes the property modal assigns (its
// class chip row) — an attribute on only one side can never match and is dead weight.
// Labels mirror the seeded vocab (0078) so the property modal doesn't need the
// features fetch just for eight stable chips.
const APT_CLASS = [
  ['class_1_incomplete', 'დაუსრულებელი / ნაკლული'], ['class_2_soviet', 'ძველი საბჭოთა'],
  ['class_3_clean', 'სუფთა ბინა'], ['class_4_econ_standard', 'ეკონომ-სტანდარტი'],
  ['class_5_modern_comfort', 'თანამედროვე კომფორტი'], ['class_6_premium', 'პრემიუმი'],
  ['class_7_designer', 'დიზაინერული'], ['class_8_ultra_lux', 'ულტრა-ლუქსი'],
]
// NOT-habitable states — mutually exclusive with any class: a frame or an unbuilt
// building cannot have an apartment class by definition (class = livable quality).
const PROP_STATE = [
  ['cls_under_constr', 'მშენებარე'], ['frame_black', 'შავი კარკასი'],
  ['frame_white', 'თეთრი კარკასი'], ['frame_white_plus', 'თეთრი პლიუსი'],
  ['frame_green', 'მწვანე კარკასი'], ['frame_needs_work', 'სარემონტო'],
]
// Building project series — the CLAIM enum (core/property-fields.js VOCAB.project), saved as
// facts.project → claim `project`, NOT a feature tag like the two ladders above. Values are
// the Georgian portal labels themselves (what the scrapers capture), so agent-recorded and
// scraped observations meet on identical strings. გუდაური and სახელმწიფო are deliberately
// ABSENT even though the claim vocab still admits them: owner rulings 2026-07-20
// (migrations 0150/0151) — neither is a building project; offering them here would let
// agents record a fabricated series.
const PROJECT_TYPES = [
  'ხრუშჩოვის', 'ჩეხური', 'ლვოვის', 'ლენინგრადის', 'მოსკოვის',
  'ქართული', 'თუხარელის', 'ყავლაშვილის', 'იტალიური ეზო',
  'საერთო საცხოვრებელი', 'დუპლექსი', 'ტრიპლექსი', 'm2-ის კომპლექსი',
  'OPTIMA m2-ისგან', 'METRA PARK', 'არასტანდარტული',
]
const QUALITY_RENT_CODES = APT_CLASS.map(([code]) => code)
const QUALITY_BUY_CODES  = [...PROP_STATE.map(([code]) => code),
  ...APT_CLASS.slice(3).map(([code]) => code)]
// Top real-world exception items clients single out AFTER stating a level (heating is #1
// for expats — "karma" wall heaters vs central; oven/bathtub have no vocab codes yet).
const DEALBREAKER_CODES = ['heat_central', 'air_conditioner', 'dishwasher', 'balcony', 'elevator', 'parking', 'room_study', 'furn_none']

// Per-property-type quick chips (Tier A, 2026-07-11) — the codes an agent actually
// gets asked about for THAT type, surfaced without digging through the 300-tag search.
// Codes live in the feature table (most pre-existed; garage_*/use_*/com_legal_address/
// com_semi_basement/land_status_conversion seeded 2026-07-11) — labels resolve via
// featureName from the /features fetch, so DB renames propagate. Same must→not→off
// cycle as the dealbreaker row; matching rides must/nice/not arrays, zero scorer work.
const TYPE_CHIP_CODES = {
  land: ['land_non_agricultural', 'land_agricultural', 'land_status_conversion', 'land_all_utilities',
    'utility_electricity', 'utility_water', 'utility_gas', 'utility_sewage',
    'land_road_paved', 'land_road_direct', 'land_level', 'land_slope', 'construction_rights', 'has_gap'],
  // ⚠️ 'garage' NOT 'garage_standalone' (fixed 2026-07-22). garage_standalone has never
  // existed in the feature table — an agent ticking it had the answer silently dropped by
  // codeList(..., fSet) validation. The capital-garage concept is already carried by the
  // `garage` code, whose aliases are ["lb_garage", "ავტოფარეხი", "კაპიტალური ავტოფარეხი"].
  // Do not "add the missing code" — that would create a duplicate of an existing concept.
  garage: ['garage_underground', 'garage', 'garage_yard', 'garage_suv_fit', 'garage_remote',
    'sec_barrier', 'sec_cameras', 'sec_guard', 'garage_storage_use'],
  commercial: ['com_vitrage', 'com_street_entrance', 'com_semi_basement', 'com_high_traffic',
    'com_three_phase', 'com_loading_dock', 'com_industrial_vent', 'com_legal_address',
    'use_office', 'use_retail', 'use_food_beverage', 'use_medical', 'use_warehouse'],
  // Apart-hotel (owner 2026-10-02, core 1940): an apartment client MATCHES apart-hotel units by
  // default — they are apartments. This chip is how a client says otherwise: must = wants one
  // (scored up), not = refuses one (excluded). Same must→not→off cycle as every row here.
  apartment: ['type_aparthotel_unit'],
}
TYPE_CHIP_CODES.office = TYPE_CHIP_CODES.commercial
// House/cottage don't get their own row — the yard items just JOIN the standard
// dealbreaker chips (an apartment client never sees pool/garden).
const HOUSE_EXTRA_DEALBREAKERS = ['pool', 'garden', 'fenced_yard']
// Property-modal (SUPPLY) twin of TYPE_CHIP_CODES — hardcoded ka labels like APT_CLASS
// (same reason: the modal has no features fetch; labels mirror the seeded vocab).
// Saved via the ingest tagKeys path → commit_feature, so demand chips match them 1:1.
const PROP_TYPE_TAGS = {
  land: [['land_non_agricultural', 'არასასოფლო'], ['land_agricultural', 'სასოფლო'],
    ['land_status_conversion', 'სტატუსი იცვლება'], ['land_all_utilities', 'ყველა კომუნიკაცია'],
    ['utility_electricity', 'დენი'], ['utility_water', 'წყალი'], ['utility_gas', 'გაზი'], ['utility_sewage', 'კანალიზაცია'],
    ['land_road_paved', 'მოასფალტებული გზა'], ['land_road_direct', 'გზისპირა'],
    ['land_level', 'სწორი რელიეფი'], ['land_slope', 'დაქანებული'],
    ['construction_rights', 'მშენებლობის უფლება'], ['has_gap', 'გ.ა.პ.']],
  garage: [['garage_underground', 'მიწისქვეშა პარკინგი'], ['garage', 'კაპიტალური ავტოფარეხი'],
    ['garage_yard', 'ეზოს პარკინგი'], ['garage_suv_fit', 'ჯიპისთვის'], ['garage_remote', 'პულტით'],
    ['sec_barrier', 'შლაგბაუმი'], ['sec_cameras', 'კამერები'], ['sec_guard', 'დაცვა'],
    ['garage_storage_use', 'საწყობად ვარგისი']],
  commercial: [['com_vitrage', 'ვიტრინა'], ['com_street_entrance', 'ქუჩიდან შესასვლელი'],
    ['com_semi_basement', 'სარდაფი/ნახევარსარდაფი'], ['com_high_traffic', 'გამვლელი ხალხი'],
    ['com_three_phase', 'სამფაზიანი დენი'], ['com_loading_dock', 'სატვირთო მისასვლელი'],
    ['com_industrial_vent', 'გამწოვი/ვენტილაცია'], ['com_legal_address', 'იურიდიული მისამართი'],
    ['use_office', 'ოფისისთვის'], ['use_retail', 'მაღაზიისთვის'], ['use_food_beverage', 'კვების ობიექტისთვის'],
    ['use_medical', 'სამედიცინო'], ['use_warehouse', 'საწყობისთვის']],
  house: [['pool', 'აუზი'], ['garden', 'ბაღი/გამწვანება'], ['fenced_yard', 'შემოღობილი ეზო']],
  // Apart-hotel (owner 2026-10-02): the unit stays an apartment; this tag records that its
  // building is run as an apart-hotel. Label = the registry's name (core 1940).
  apartment: [['type_aparthotel_unit', 'აპარტოტელის ნომერი']],
}
PROP_TYPE_TAGS.office = PROP_TYPE_TAGS.commercial
PROP_TYPE_TAGS.cottage = PROP_TYPE_TAGS.house

// Nationality/citizenship policy codes — subcategory-driven (0081), hardcoded
// fallback for a stale server build whose /features doesn't return the field.
// Shared by the SALE restrictions panel (seller side) and the BUYER profile
// (demand side): sale conditions are citizenship-only, never pets/smokers.
const NAT_CODE_FALLBACK = new Set(['pol_georgians', 'pol_foreigners', 'pol_rus_ukr_bel', 'pol_ukrainians',
  'pol_indians', 'pol_arabs', 'pol_chinese', 'pol_iranians', 'pol_pakistanis',
  'pol_turks', 'pol_russians', 'pol_israelis'])
const isNationalityFeature = f => ('subcategory' in f) ? f.subcategory === 'nationality' : NAT_CODE_FALLBACK.has(f.code)

// ── ⓘ definitions (0162) ─────────────────────────────────────────────────────
// Owner: "little i in circle that shows definition when PRESSED — on mobile hover is
// impossible." Definitions live on feature.description_ka/_en (served by /api/core/features);
// a chip whose feature has no description simply renders no dot, so coverage grows with the
// dictionary and never blocks a ladder. `key` may be a code (quality/frame ladders) OR a raw
// Georgian label (PROJECT_TYPES stores portal strings) — resolved via name/alias either way.
function featureDefOf(features, key) {
  if (!key || !features?.length) return null
  const k = String(key).toLowerCase()
  const f = features.find(x => x.code === key)
    || features.find(x => (x.name_ka || '').toLowerCase() === k || (x.name_en || '').toLowerCase() === k
        || (x.aliases || []).some(a => String(a).toLowerCase() === k))
  if (!f) return null
  const ka = getLanguage() === 'ka'
  const text = ka ? (f.description_ka || f.description_en) : (f.description_en || f.description_ka)
  if (!text) return null
  return { text, source: f.description_source, name: ka ? (f.name_ka || f.name_en) : (f.name_en || f.name_ka) }
}

// Sits INSIDE the chip button — stopPropagation so tapping ⓘ never toggles the chip itself.
function InfoDot({ active, onTap }) {
  return (
    <span role="button" tabIndex={-1} aria-label="definition"
      onClick={e => { e.stopPropagation(); e.preventDefault(); onTap() }}
      className={`ml-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border text-xs leading-none align-[-1px] shrink-0 ${active ? 'bg-primary text-primary-foreground border-primary' : 'border-current opacity-60'}`}>
      i
    </span>
  )
}

// One panel under the chip row (not a floating popover — no positioning pain, works on
// mobile, and the open definition is impossible to miss).
function FeatureDefPanel({ def, onClose }) {
  if (!def) return null
  return (
    <div className="mt-1.5 rounded-md border border-border bg-muted/50 p-2 pr-6 text-xs relative">
      <div className="font-semibold mb-0.5">
        {def.name}
        {def.source === 'ai' && <span className="ml-1.5 font-normal text-xs text-muted-foreground uppercase">ai</span>}
      </div>
      <div className="text-muted-foreground whitespace-pre-wrap">{def.text}</div>
      <button type="button" onClick={onClose} className="absolute top-1 right-1.5 text-muted-foreground">✕</button>
    </div>
  )
}

// ── Near points (B220, 0267) — proximity as a demand fact ─────────────────────────────
// ── ComplexPickerControl (0381 follow-up, 2026-08-03) ────────────────────────
// "In THIS development" — a separate criterion from proximity, never a radius (the
// scorer answers it from the complex linker's evidence). Several picks mean ANY of
// them, the same reading the fill form and the near-point group use.
//
// This control exists because the write path had no retract: the client fill form
// could set complex_codes and nothing in the Desk could clear it, while the link
// itself is single-use so the client could not correct their own tap either.
// Removal is therefore the load-bearing half — the search is the convenience.
//
// Names are resolved through the SAME endpoint that searches, so a value that
// arrived from the fill form still renders as a name rather than a uuid; an id
// with no row left (a deleted complex) keeps a short uuid and stays removable
// instead of vanishing silently.
const complexNameCache = new Map()
function ComplexPickerControl({ value, onChange }) {
  const ids = Array.isArray(value) ? value : []
  const [names, setNames] = useState(() => new Map(complexNameCache))
  const [q, setQ] = useState('')
  const [hits, setHits] = useState([])
  useEffect(() => {
    const missing = ids.filter(id => !complexNameCache.has(id))
    if (!missing.length) return
    let cancelled = false
    getComplexesByIds(missing).then(rows => {
      if (cancelled) return
      for (const c of rows) complexNameCache.set(c.id, c.name)
      setNames(new Map(complexNameCache))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [ids.join(',')])
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return }
    let cancelled = false
    const timer = setTimeout(() => {
      searchComplexes(q).then(rows => {
        if (cancelled) return
        for (const c of rows) complexNameCache.set(c.id, c.name)
        setHits(rows.filter(c => !ids.includes(c.id)).slice(0, 6))
      }).catch(() => setHits([]))
    }, 200)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [q, ids.join(',')])
  const label = id => names.get(id) || complexNameCache.get(id) || `${id.slice(0, 8)}…`
  // null, not [], when the last one goes: diffCriteriaOps compares arrays and an
  // empty array is the honest "no complex wanted" either way, but null keeps the
  // stored row clean.
  const remove = id => { const next = ids.filter(x => x !== id); onChange(next.length ? next : null) }
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{t('desk_complex_hint')}</p>
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {ids.map(id => (
            <button key={id} type="button" onClick={() => remove(id)}
              className={pill('neutral', { door: true, extra: 'text-muted-foreground hover:bg-muted' })}
              title={t('desk_complex_remove')}>🏢 {label(id)} ✕</button>
          ))}
        </div>
      )}
      {ids.length < 5 && (
        <input className={input} value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('desk_complex_search_ph')} />
      )}
      {hits.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {hits.map(c => (
            <button key={c.id} type="button"
              onClick={() => { onChange([...ids, c.id]); setQ(''); setHits([]) }}
              className={pill('neutral', { door: true, extra: 'hover:bg-muted' })}
              title={c.developer || c.district_code || ''}>+ {c.name}</button>
          ))}
        </div>
      )}
    </div>
  )
}

// Writes form.near_points = [{label, lat, lng, radius_m, proximity_kind}] — the SAME point
// shape the search's near token resolves to, fed from the SAME gazetteer (locations
// dictionary), so the requirement and the search can never mean different things by "near".
// Two tiers (owner ruling 2026-07-24): walk (ფეხით — what a filter means; metro 300m) and
// bus (ავტობუსით — card context; metro 1500m). Defaults come from the two ruled maps and
// are applied AT CAPTURE; the radius stays editable because a client-stated number ("5
// minutes' walk" ≈ 400m) beats the tier default. Kept to 5 points (server clamp mirrors).
let nearLocsCache = null
function NearPointsControl({ value, onChange }) {
  const pts = Array.isArray(value) ? value : []
  const [locs, setLocs] = useState(nearLocsCache || [])
  const [q, setQ] = useState('')
  useEffect(() => {
    if (nearLocsCache) return
    getLocations().then(rows => { nearLocsCache = rows; setLocs(rows) }).catch(() => {})
  }, [])
  const norm = s => String(s || '').toLowerCase()
  const suggestions = q.trim().length >= 2
    ? locs.filter(l => l.lat != null && l.lng != null
        && (norm(l.name).includes(norm(q))
            || (l.meta?.aliases || []).some(a => norm(a).includes(norm(q)))))
        .slice(0, 6)
    : []
  const radiusFor = (type, kind) => kind === 'bus'
    ? (LOC_RADIUS_BY_TYPE[type] || 1500)
    : (NEAR_SEARCH_RADIUS_M[type] || 1000)
  const addPoint = loc => {
    if (pts.length >= 5) return
    onChange([...pts, { label: loc.name, lat: Number(loc.lat), lng: Number(loc.lng),
      radius_m: radiusFor(loc.type, 'walk'), proximity_kind: 'walk', _type: loc.type }])
    setQ('')
  }
  const patch = (i, up) => onChange(pts.map((p, j) => j === i ? { ...p, ...up } : p))
  const remove = i => { const next = pts.filter((_, j) => j !== i); onChange(next.length ? next : null) }
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{t('desk_near_hint')}</p>
      {pts.map((p, i) => (
        <div key={`${p.label}|${i}`} className="flex items-center gap-2 py-1 text-xs">
          <span className="font-medium truncate flex-1">{p.label}</span>
          <button type="button"
            className={pill('neutral', { door: true, extra: 'text-muted-foreground hover:bg-muted' })}
            title={t('desk_near_tier_toggle')}
            onClick={() => {
              const kind = p.proximity_kind === 'walk' ? 'bus' : 'walk'
              // toggling a tier re-applies that tier's default — a hand-edited radius is a
              // client statement, but changing the tier changes the question being asked.
              patch(i, { proximity_kind: kind, radius_m: radiusFor(p._type, kind) })
            }}>
            {p.proximity_kind === 'bus' ? t('desk_near_bus') : t('desk_near_walk')}
          </button>
          <input type="number" min="50" max="10000" step="50" value={p.radius_m}
            className="w-20 rounded border border-input px-1.5 py-0.5 bg-background text-right"
            onChange={e => patch(i, { radius_m: e.target.value === '' ? '' : Number(e.target.value) })} />
          <span className="text-xs text-muted-foreground">{t('desk_near_meters')}</span>
          <button type="button" className="text-muted-foreground hover:text-destructive cursor-pointer" onClick={() => remove(i)}>✕</button>
        </div>
      ))}
      {pts.length < 5 && (
        <div className="relative">
          <input className={input} placeholder={t('desk_near_add_ph')} value={q} onChange={e => setQ(e.target.value)} />
          {suggestions.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-lg p-1">
              {suggestions.map(l => (
                <button key={l.id} type="button"
                  className="w-full text-left px-2 py-1 text-xs rounded hover:bg-muted cursor-pointer"
                  onClick={() => addPoint(l)}>
                  <span className="font-medium">{l.name}</span>
                  <span className="text-muted-foreground"> · {l.type}{l.district ? ` · ${l.district}` : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CriteriaFieldsForm({ form, set, districts, features, intent, propertyCategory }) {
  const featureName  = code => { const f = features.find(f => f.code === code); return f ? (f.name_ka || f.name_en || code) : code }
  const [defKey, setDefKey] = useState(null)   // ⓘ: which quality-ladder term is open
  // Household self-description reuses the landlord-side tenant_terms vocab (0076) so
  // demand ("we ARE a family with a cat") and supply ("families allowed, cats allowed")
  // stay directly comparable at match time. Renters get the full vocab; BUYERS get
  // the nationality subset only (B35) — the mirror of the seller's 0080/0081
  // restrictions, which are citizenship-shaped, never pets/smokers.
  const isRentish = !intent || ['rent', 'short_term_rent'].includes(intent)
  const isShortTerm = intent === 'short_term_rent'
  const isBuy = intent === 'buy'
  // ACTIVE codes, plus any retired one this client already carries (2026-08-05). 11 of the
  // 48 tenant_terms codes are retired (languages became nationality at 0400, residency at
  // 0421, …) and offering them here would describe NEW clients in a vocabulary the matcher
  // no longer speaks. But a client saved before the retirement still carries the code, and
  // hiding it would make their chip un-clearable — the exact lie the property panel's
  // read-only badge exists to prevent. So: offer active, keep what is set.
  // `active` is undefined against a server older than this deploy → everything stays.
  const tenantTermsVocab = features.filter(f => f.category === 'tenant_terms'
    && (f.active !== false || (form.household_codes || []).includes(f.code)))

  // Property-type gating (planning doc "Gate Fields by Property Type × Deal Type"):
  // residential (apartment/house/cottage/other/unset) keeps the form as-is; the other
  // shapes hide sections that don't map onto them (no bedroom count for land, no
  // condition ladder for a garage, etc). Unset/'other' falls back to unrestricted
  // residential-shaped rendering deliberately — it's the catch-all.
  const isLandLike = propertyCategory === 'land'
  const isGarage = propertyCategory === 'garage'
  const isCommercialLike = ['commercial', 'office'].includes(propertyCategory)
  const isHotel = propertyCategory === 'hotel'
  // No household concept for anything non-residential.
  const hideHousehold = isLandLike || isGarage || isCommercialLike || isHotel
  // No bedroom count outside residential; commercial/hotel keep rooms_min/max (sqm-ish
  // space count / room-and-key count respectively), land/garage hide both.
  const hideBedrooms = isLandLike || isGarage || isCommercialLike || isHotel
  const hideRooms = isLandLike || isGarage
  // Land/garage have no condition concept at all.
  // ⚠️ commercial/office were ALSO hidden here until 2026-07-22, on the grounds that
  // neither ladder models commercial fit-out. Owner ruling reversed it: businesses
  // routinely take a შავი/თეთრი კარკასი and fit it out to brand spec — a restaurant
  // wants raw space for heavy HVAC, an IT tenant wants a white frame for cabling. The
  // client fill form asks this for commercial (5 chips) and office (4 — no black
  // frame), so hiding it here would put the two surfaces out of sync. See
  // FILL_FORM_SPEC.md §2.4. Hotel stays hidden (star rating is genuinely new vocab).
  const hideQualityLadder = isLandLike || isGarage || isHotel
  // Residential amenity chips (heating/AC/dishwasher/balcony/elevator/parking/study/
  // furnished) don't apply to land, and a garage IS the parking so none apply either.
  const hideDealbreakers = isLandLike || isGarage
  // land_sqm_min/max (plot/yard size — columns existed since 0017/0039, AI parser
  // already writes them; this UI was the missing link). For raw land the plot IS
  // the property, so the building-area pair hides and land becomes the only size.
  const showLandArea = ['house', 'cottage', 'land'].includes(propertyCategory)
  const hideBuildingArea = isLandLike
  // Tier A per-type quick chips + house/cottage yard extras on the dealbreaker row
  const typeChipCodes = TYPE_CHIP_CODES[propertyCategory] || []
  const dealbreakerCodes = ['house', 'cottage'].includes(propertyCategory)
    ? [...DEALBREAKER_CODES, ...HOUSE_EXTRA_DEALBREAKERS] : DEALBREAKER_CODES

  const householdVocab = isRentish ? tenantTermsVocab : tenantTermsVocab.filter(isNationalityFeature)

  // District tiers share one picker; setTier moves the code between the three arrays.
  const districtTierOf = code =>
    (form.district_must_codes || []).includes(code) ? 'must'
    : (form.district_codes || []).includes(code) ? 'nice'
    : (form.excluded_district_codes || []).includes(code) ? 'not' : null
  const setDistrictTier = (code, tier) => {
    set('district_must_codes', (form.district_must_codes || []).filter(c => c !== code).concat(tier === 'must' ? [code] : []))
    set('district_codes', (form.district_codes || []).filter(c => c !== code).concat(tier === 'nice' ? [code] : []))
    set('excluded_district_codes', (form.excluded_district_codes || []).filter(c => c !== code).concat(tier === 'not' ? [code] : []))
  }
  const districtOptions = districts.map(d => ({ code: d.code, label: d.name_ka || d.name_en || d.code,
    search: [d.name_ka, d.name_en, d.name_ru, d.code].filter(Boolean).join(' '),
    group: d.city || t('desk_district_group_other') }))

  // Feature tiers: same three arrays as always, driven by chips instead of dropdowns.
  const featureTierOf = code =>
    (form.must_have_codes || []).includes(code) ? 'must'
    : (form.nice_to_have_codes || []).includes(code) ? 'nice'
    : (form.must_not_have_codes || []).includes(code) ? 'not' : null
  const setFeatureTier = (code, tier) => {
    set('must_have_codes', (form.must_have_codes || []).filter(c => c !== code).concat(tier === 'must' ? [code] : []))
    set('nice_to_have_codes', (form.nice_to_have_codes || []).filter(c => c !== code).concat(tier === 'nice' ? [code] : []))
    set('must_not_have_codes', (form.must_not_have_codes || []).filter(c => c !== code).concat(tier === 'not' ? [code] : []))
  }
  const featureOptions = features
    .filter(f => f.category !== 'tenant_terms')
    .map(f => ({ code: f.code, label: f.name_ka || f.name_en || f.code,
      search: [f.name_ka, f.name_en, f.name_ru, f.code].filter(Boolean).join(' '),
      group: f.category }))

  // Quality level: single-select over condition_codes (tap again to clear).
  const qualityCodes = isRentish ? QUALITY_RENT_CODES : QUALITY_BUY_CODES
  const qualityLevel = (form.condition_codes || [])[0] || null
  return (
    <>
      <div className={card}>
        <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_criteria_section_price_size')}</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-muted-foreground">{t('desk_criteria_price_min')}<input type="number" className={input} value={form.price_min ?? ''} onChange={e => set('price_min', e.target.value === '' ? null : Number(e.target.value))} /></label>
          <label className="text-xs text-muted-foreground">{t('desk_criteria_price_max')}<input type="number" className={input} value={form.price_max ?? ''} onChange={e => set('price_max', e.target.value === '' ? null : Number(e.target.value))} /></label>
          <label className="text-xs text-muted-foreground">{t('desk_criteria_currency')}
            {/* USD/GEL only — we do not transact in EUR (0 rows anywhere), and a
                currency with no FX path gives the scorer a budget it cannot compare. */}
            <select className={input} value={form.currency || 'USD'} onChange={e => set('currency', e.target.value)}>
              <option>USD</option><option>GEL</option>
            </select>
          </label>
          <label className="text-xs text-muted-foreground">{t('desk_criteria_max_drive')}<input type="number" className={input} value={form.max_drive_minutes ?? ''} onChange={e => set('max_drive_minutes', e.target.value === '' ? null : Number(e.target.value))} /></label>
          {!hideBuildingArea && (<>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_area_min')}<input type="number" className={input} value={form.area_sqm_min ?? ''} onChange={e => set('area_sqm_min', e.target.value === '' ? null : Number(e.target.value))} /></label>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_area_max')}<input type="number" className={input} value={form.area_sqm_max ?? ''} onChange={e => set('area_sqm_max', e.target.value === '' ? null : Number(e.target.value))} /></label>
          </>)}
          {showLandArea && (<>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_land_min')}<input type="number" className={input} value={form.land_sqm_min ?? ''} onChange={e => set('land_sqm_min', e.target.value === '' ? null : Number(e.target.value))} /></label>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_land_max')}<input type="number" className={input} value={form.land_sqm_max ?? ''} onChange={e => set('land_sqm_max', e.target.value === '' ? null : Number(e.target.value))} /></label>
          </>)}
          {/* 0083: min dedicated power feed — the silent commercial deal-killer (gyms,
              salons, F&B): old buildings often carry only 5-15 kW. */}
          {isCommercialLike && (
            <label className="text-xs text-muted-foreground">{t('desk_criteria_power_min')}<input type="number" className={input} value={form.required_power_kw ?? ''} onChange={e => set('required_power_kw', e.target.value === '' ? null : Number(e.target.value))} /></label>
          )}
          {!hideRooms && (<>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_rooms_min')}<input type="number" step="0.5" className={input} value={form.rooms_min ?? ''} onChange={e => set('rooms_min', e.target.value === '' ? null : Number(e.target.value))} /></label>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_rooms_max')}<input type="number" step="0.5" className={input} value={form.rooms_max ?? ''} onChange={e => set('rooms_max', e.target.value === '' ? null : Number(e.target.value))} /></label>
          </>)}
          {!hideBedrooms && (<>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_bedrooms_min')}<input type="number" step="0.5" className={input} value={form.bedrooms_min ?? ''} onChange={e => set('bedrooms_min', e.target.value === '' ? null : Number(e.target.value))} /></label>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_bedrooms_max')}<input type="number" step="0.5" className={input} value={form.bedrooms_max ?? ''} onChange={e => set('bedrooms_max', e.target.value === '' ? null : Number(e.target.value))} /></label>
          </>)}
          {/* Floor position (B43): min/max + not-ground / not-top. The scorer compares
              these against property.floor/floors_total (SOFT checks). Was AI-parse-only —
              plumbing (_write_criteria, v_allowed) already existed. Not-first/last write
              true|null (never false: no floor pref ≠ "ground floor is fine"). Building
              concept, so hidden for land/garage like rooms. */}
          {!hideRooms && (<>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_floor_min')}<input type="number" min="0" className={input} value={form.floor_min ?? ''} onChange={e => set('floor_min', e.target.value === '' ? null : Number(e.target.value))} /></label>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_floor_max')}<input type="number" min="0" className={input} value={form.floor_max ?? ''} onChange={e => set('floor_max', e.target.value === '' ? null : Number(e.target.value))} /></label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={!!form.floor_not_first} onChange={e => set('floor_not_first', e.target.checked || null)} />
              {t('desk_criteria_floor_not_first')}
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={!!form.floor_not_last} onChange={e => set('floor_not_last', e.target.checked || null)} />
              {t('desk_criteria_floor_not_last')}
            </label>
          </>)}
        </div>
      </div>

      <div className={card}>
        <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_criteria_section_districts')}</p>
        {/* Same 3-tier model as features (0076): tap a chip to cycle ✓ must → ★ nice → ✗ not.
            defaultTier flipped nice→must 2026-07-20: with 'nice' as the first tap, 28/34
            live clients had a stated district recorded as a soft nudge and 0 as binding —
            "I want Vake" was matched across all 51 districts. A stated district now BINDS
            on first tap (scorer 0.7.1 softens bordering-raion misses to partial, so this
            does not hide across-the-border inventory); the second tap still reaches ★ nice
            for the genuine "would also consider" case. */}
        <p className="text-xs text-muted-foreground mb-2">{t('desk_tier_legend')}</p>
        <TierTagPicker options={districtOptions} tierOf={districtTierOf} setTier={setDistrictTier}
          defaultTier="must" placeholder={t('desk_criteria_district_search_ph')} />
        {/* Near points (B220) — composes WITH districts, never replaces them ("in Vake AND
            near metro"). All points must hold at match time. */}
        <p className="text-xs font-semibold text-muted-foreground mt-3 mb-1">{t('desk_near_label')}</p>
        <NearPointsControl value={form.near_points} onChange={v => set('near_points', v)} />
        {/* Complex (0381) — deliberately BELOW near points and visually separate: "in
            this building" and "near this place" are different asks, and reading them
            as one is the confusion the fill form's own hint had to correct. */}
        <p className="text-xs font-semibold text-muted-foreground mt-3 mb-1">{t('desk_complex_label')}</p>
        <ComplexPickerControl value={form.complex_codes} onChange={v => set('complex_codes', v)} />
      </div>

      {!hideHousehold && (
      <div className={card}>
        <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_criteria_section_household')}</p>
        <MultiSelect label={t('desk_criteria_household_tags')} options={householdVocab.map(f => f.name_ka || f.name_en || f.code)}
          selected={(form.household_codes || []).map(c => { const f = householdVocab.find(f => f.code === c); return f ? (f.name_ka || f.name_en || c) : c })}
          onChange={names => set('household_codes', names.map(n => householdVocab.find(f => (f.name_ka || f.name_en || f.code) === n)?.code).filter(Boolean))} />
        <div className="grid grid-cols-2 gap-3 mt-2">
          {isRentish && (<>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_occupants')}<input type="number" min="1" className={input} value={form.occupants ?? ''} onChange={e => set('occupants', e.target.value === '' ? null : Number(e.target.value))} /></label>
            <label className="text-xs text-muted-foreground">{t('desk_criteria_children_ages')}
              <input className={input} placeholder="5, 12" value={(form.children_ages || []).join(', ')}
                onChange={e => set('children_ages', e.target.value.split(/[,\s]+/).map(Number).filter(n => Number.isInteger(n) && n >= 0 && n < 18))} />
            </label>
            {/* has_pets / has_children: rent-side booleans the scorer checks against owner
                terms (pets_allowed/children_allowed, 0080). Plumbing (EDITABLE_CRITERIA_FIELDS,
                _write_criteria) already existed — only the input was missing. Unchecked = null
                (unknown), never false: an empty client profile scores unknown, not "no pets". */}
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={!!form.has_pets} onChange={e => set('has_pets', e.target.checked || null)} />
              {t('desk_crit_has_pets')}
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={!!form.has_children} onChange={e => set('has_children', e.target.checked || null)} />
              {t('desk_crit_has_children')}
            </label>
            {/* The two move-in money ceilings (B202 part 2, 2026-07-27). The scorer has
                checked both since v0.10.0 and the gateway has accepted both for far
                longer, but the only writers were the client's own fill link and the AI
                parse — so an agent taking a client by phone could neither record "can't
                put down more than two months" nor correct the AI when it guessed. Two
                fields, not one total: a deposit is refundable and prepaid rent is not,
                which is why the client record separates them and the scorer checks them
                apart. Blank = unknown, never 0 — "no ceiling stated" and "cannot pay
                anything up front" are different clients. */}
            <label className="text-xs text-muted-foreground">{t('desk_crit_max_deposit_months')}
              <input type="number" min="0" step="0.5" className={input} value={form.max_deposit_months ?? ''}
                onChange={e => set('max_deposit_months', e.target.value === '' ? null : Number(e.target.value))} />
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_crit_prepayment_months')}
              <input type="number" min="0" step="1" className={input} value={form.prepayment_months ?? ''}
                onChange={e => set('prepayment_months', e.target.value === '' ? null : Number(e.target.value))} />
            </label>
          </>)}
          {/* move-in stays for BUY too: the scorer compares it against the seller's
              handover terms (0080). occupants/children are rent concepts. */}
          <label className="text-xs text-muted-foreground">{t(isShortTerm ? 'desk_criteria_check_in' : 'desk_criteria_move_in')}<DateInput className={`flex items-center gap-0.5 ${input}`} value={form.move_in_date || ''} onChange={v => set('move_in_date', v || null)} /></label>
          {/* 0082: short-term stays are a date RANGE (move_in doubles as check-in) —
              the gateway CHECK enforces check_out > move_in, min= mirrors it in the UI */}
          {isShortTerm && (
            <label className="text-xs text-muted-foreground">{t('desk_criteria_check_out')}<DateInput className={`flex items-center gap-0.5 ${input}`} min={form.move_in_date || undefined} value={form.check_out_date || ''} onChange={v => set('check_out_date', v || null)} /></label>
          )}
          {/* mortgage_ready: BUY-side boolean → the scorer's cash_only/appraisal gate (0080).
              Same pre-existing plumbing; only the input was missing (was AI-parse-only). */}
          {isBuy && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={!!form.mortgage_ready} onChange={e => set('mortgage_ready', e.target.checked || null)} />
              {t('desk_crit_mortgage_ready')}
            </label>
          )}
          {/* needs_deeded_parking (0167, spec §2.10): the space is CONVEYED, not merely
              available — the `parking` feature code is the availability question.
              Deliberately broader than the client link form, which only asks on
              apartment/office: the agent knows when it applies, an inexperienced client
              does not, and both write the same criteria field either way. */}
          {isBuy && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={!!form.needs_deeded_parking} onChange={e => set('needs_deeded_parking', e.target.checked || null)} />
              {t('desk_crit_parking_deeded')}
            </label>
          )}
          {/* payment_currency deliberately NOT here — currency terms are the OWNER's to state
              (listing side); a client-side preference just gets negotiated. Column stays dormant. */}
        </div>
      </div>
      )}

      <div className={card}>
        {!hideQualityLadder && (<>
          <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_criteria_section_quality')}</p>
          {/* Clients state an overall LEVEL ("ევრორემონტი"), not item lists. One tap; tap again to clear.
              Renters get the renovation ladder, buyers the frame ladder → condition_codes. */}
          <div className="flex flex-wrap gap-1.5">
            {qualityCodes.map(code => (
              <button key={code} type="button"
                onClick={() => set('condition_codes', qualityLevel === code ? [] : [code])}
                className={pill('none', { door: true, extra: qualityLevel === code ? TIER_STYLE.must : 'border-border text-muted-foreground hover:bg-muted' })}>
                {featureName(code)}
                {featureDefOf(features, code) && (
                  <InfoDot active={defKey === code} onTap={() => setDefKey(k => k === code ? null : code)} />
                )}
              </button>
            ))}
          </div>
          <FeatureDefPanel def={featureDefOf(features, defKey)} onClose={() => setDefKey(null)} />
        </>)}
        {/* Tier A: type-specific chips (land/garage/commercial) — same menu as dealbreakers */}
        {typeChipCodes.length > 0 && (<>
          <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_criteria_type_features')}</p>
          <div className="flex flex-wrap gap-1.5">
            {typeChipCodes.map(code => (
              <TierMenu key={code} code={code} label={featureName(code)}
                tier={featureTierOf(code)} setTier={setFeatureTier} />
            ))}
          </div>
        </>)}
        {/* The one-or-two exception items clients actually single out. */}
        {!hideDealbreakers && (<>
          <p className="text-xs font-semibold text-muted-foreground mt-3 mb-1">{t('desk_criteria_dealbreakers')}</p>
          <div className="flex flex-wrap gap-1.5">
            {dealbreakerCodes.map(code => (
              <TierMenu key={code} code={code} label={featureName(code)}
                tier={featureTierOf(code)} setTier={setFeatureTier} />
            ))}
          </div>
        </>)}
        {/* Full vocab, OPEN by default (owner 2026-07-24: "no need to have closed that
            part"). It was collapsed to keep the form short, but the search box IS the thing
            that keeps 300 tags navigable — hiding the search behind a disclosure meant an
            agent had to know it existed before they could look anything up. `open` on
            <details> keeps the fold available without making it the default state. */}
        <details className="mt-3" open>
          <summary className="text-xs text-muted-foreground cursor-pointer">{t('desk_criteria_more_features')}</summary>
          <div className="mt-2">
            <p className="text-xs text-muted-foreground mb-1">{t('desk_tier_legend')}</p>
            <TierTagPicker options={featureOptions} tierOf={featureTierOf} setTier={setFeatureTier}
              defaultTier="must" placeholder={t('desk_criteria_feature_search_ph')} />
          </div>
        </details>
      </div>

      <div className={card}>
        <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_criteria_section_search_area')}</p>
        <PolygonMapPicker value={form.search_polygon} onChange={v => set('search_polygon', v)} />
      </div>
    </>
  )
}

// Handles BOTH a plain manual "Add client" AND an AI-parsed draft — one editable form either way,
// exactly like ManualPropertyModal. When `draft` (an extract_requirement command) is passed, the
// fields come pre-filled and Save = "accept & commit"; empty otherwise. This replaced the old
// read-only ClientReview badge view for CREATE so the AI-opened client matches the property modal
// (2026-07-03). ClientReview is still used for the AMEND case (its ops-diff view has no form analog).
function ManualClientModal({ districts, features, draft, intakeId, rawText, decision, confidence, onClose, onSaved, agentId, agentName = null, presetParty = null }) {
  const isDraft = !!draft
  // "Unknown contact" is normalizeEnvelope's honest placeholder for a nameless message — show it
  // as an empty Name field to fill, not as a literal client name.
  const draftName = draft?.person?.display_name && draft.person.display_name !== 'Unknown contact' ? draft.person.display_name : ''
  // presetParty (B48): "+ Add request" from the person view pre-binds a NEW requirement to an
  // EXISTING party — the person fields lock and pickedPartyId rides person.party_id at commit
  // (the reuse path verified 2026-07-19), so a second request can't mint a duplicate person.
  const initParty = presetParty
    ? { name: presetParty.name || '', phone: presetParty.phone || '', email: presetParty.email || '' }
    : { name: draftName, phone: draft?.person?.phone || '', email: draft?.person?.email || '' }
  const initReq = { intent: draft?.requirement?.intent || 'rent', property_category: draft?.requirement?.property_category || 'apartment', urgency: draft?.requirement?.urgency || '', title: draft?.requirement?.title || '' }
  const initCriteria = draft?.criteria || {}
  const [party, setParty] = useState(initParty)
  const [reqInfo, setReqInfo] = useState(initReq)
  const [criteria, setCriteria] = useState(initCriteria)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  // In-modal paste→parse (same pattern as ManualPropertyModal): the AI fills the form fields,
  // the human reviews right here. parsedMeta carries the intake linkage so Save still writes
  // the audit trail (intake_id + decision) exactly like a Desk-level parse used to.
  const [pasteText, setPasteText] = useState('')
  const [parsing, setParsing] = useState(false)
  const [parsedMeta, setParsedMeta] = useState(null)   // { intakeId, decision, confidence, rawText }
  // A second paste may not silently win — the demand-side half of T40 (2026-09-02), which shipped
  // on the property modal only. See src/lib/clientParseFields.js for the field list and why the
  // comparison and the displayed text are two different strings.
  const [parseConflicts, setParseConflicts] = useState([])
  const [parseAnswers, setParseAnswers] = useState({})
  const [parseRaw, setParseRaw] = useState(null)
  // Existing-party pre-check (core 0123/0124, KNOWLEDGE_MODEL §6): when the typed phone/email
  // matches existing parties, show them — the agent's explicit pick rides the commit as
  // person.party_id and REUSES that party (their tap is the human verdict 0027 requires).
  // Silent reuse is forbidden; not picking falls through to resolve_party (new row + hypothesis).
  const [partyCands, setPartyCands] = useState([])
  const [pickedPartyId, setPickedPartyId] = useState(presetParty?.id || null)
  // review 2026-09-23: the phone/email the pick was made AT. A pick is about THAT contact, so any
  // change to phone/email — typed, pasted, or an accepted AI conflict — drops it (only typing did).
  const pickSnapRef = useRef(presetParty ? { phone: (initParty.phone || '').trim(), email: (initParty.email || '').trim() } : null)
  useEffect(() => {
    const s = pickSnapRef.current
    if (pickedPartyId && s && (party.phone.trim() !== s.phone || party.email.trim() !== s.email)) setPickedPartyId(null)
  }, [party.phone, party.email])   // eslint-disable-line react-hooks/exhaustive-deps
  // review 2026-09-23: phone/email that ARRIVE (paste parse, accepted conflict) get the same
  // existing-person check a typed one gets on blur.
  const [candCheckTick, setCandCheckTick] = useState(0)
  useEffect(() => { if (candCheckTick) checkPartyCandidates() }, [candCheckTick])   // eslint-disable-line react-hooks/exhaustive-deps

  async function checkPartyCandidates() {
    const phone = party.phone.trim(), email = party.email.trim()
    if (phone.replace(/\D/g, '').length < 6 && !email) { setPartyCands([]); return }
    try {
      const r = await findPartyCandidates({ phone: phone || null, email: email || null })
      // never offer our own bridged agents as "this client"
      setPartyCands((r.candidates || []).filter(c => !c.is_agent))
    } catch { /* pre-check is best-effort — a failure must never block manual intake */ }
  }

  // Route a bucketed patch back into the three state objects it came from.
  function applyClientPatch(patch) {
    if (patch.party) setParty(v => ({ ...v, ...patch.party }))
    if (patch.party && ('phone' in patch.party || 'email' in patch.party)) setCandCheckTick(n => n + 1)
    if (patch.reqInfo) setReqInfo(v => ({ ...v, ...patch.reqInfo }))
    if (patch.criteria) setCriteria(v => ({ ...v, ...patch.criteria }))
  }

  async function handlePasteParse() {
    if (!pasteText.trim()) return
    setParsing(true); setErr(null)
    try {
      const { intake_id, envelope: env } = await parseIntakeEnvelope({ raw_text: pasteText, channel: 'scheduler' })
      const cmd = extractRequirementCmd(env) || amendRequirementCmd(env)
      if (!cmd || cmd.type !== 'extract_requirement') {
        setErr(env?.about === 'supply'
          ? 'This reads like a PROPERTY listing, not a client — use Add property instead.'
          : 'No client demand found in this message — fill the fields by hand or paste a fuller message.')
        return
      }
      // ⚠️ WHAT THIS REPLACED, because the shape recurs: `p.phone || prev.phone` for the person
      // and `{ ...prev, ...cmd.criteria }` for the criteria. Every field the newest parse
      // mentioned won — over the previous parse AND over anything the agent had typed. The
      // property modal was fixed on 2026-09-02 and this one was missed, so the demand side kept
      // overwriting silently for two more days.
      const { patch, conflicts, raw } = splitClientParse(cmd, { party, reqInfo, criteria })
      applyClientPatch(patch)
      setParseConflicts(conflicts)
      setParseAnswers({})
      setParseRaw(raw)
      setParsedMeta({ intakeId: intake_id, decision: env.decision || 'new', confidence: env.confidence ?? null, rawText: pasteText })
      // The box empties on success so the NEXT batch of details goes in clean (owner's flow:
      // "paste another text for more details"). It is left alone on a failure — retyping a
      // message the parse could not read is the worst possible response to an error.
      setPasteText('')
    } catch (e) { setErr(e.message) }
    finally { setParsing(false) }
  }
  // Snapshot the AI's pre-fill so Save can tell "human curated it" from "accepted as-is" — the same
  // human_edited audit signal the old review card used (edit-before-commit = confirmation), no checkbox.
  const initialRef = useRef(JSON.stringify({ party: initParty, reqInfo: initReq, criteria: initCriteria }))

  const set = (field, value) => setCriteria(c => ({ ...c, [field]: value }))
  const canSave = party.name.trim().length > 0
  // Same shape rules as the public fill form, but a WARNING, not a gate: the agent
  // is transcribing someone else's number and may legitimately hold one we have no
  // rule for — refusing to save the whole client record over a phone is the worse
  // failure. A bare number is read as Georgian (fmtPhone assumes the same).
  const phoneWarn = checkPhone(...splitDial(party.phone, '+995'))

  async function handleSave() {
    if (!canSave) return
    setSaving(true); setErr(null)
    try {
      const { search_polygon, ...restCriteria } = criteria
      const edited = isDraft ? JSON.stringify({ party, reqInfo, criteria }) !== initialRef.current : true
      const cmd = {
        type: 'extract_requirement',
        person: { display_name: party.name.trim(), phone: party.phone.trim() || null, email: party.email.trim() || null, kind: draft?.person?.kind || 'person',
                  ...(pickedPartyId ? { party_id: pickedPartyId } : {}) },
        requirement: { intent: reqInfo.intent, property_category: reqInfo.property_category, urgency: reqInfo.urgency || null, title: reqInfo.title.trim() || null },
        criteria: { ...restCriteria, ...(search_polygon ? { search_polygon: JSON.stringify(search_polygon) } : {}) },
        human_edited: edited,
      }
      // AI draft carries the real intake_id + decision so the committed proposal links to the parsed
      // message (audit trail); a manual add has no intake and is decision 'manual'.
      // Three provenance shapes, one save path: a Desk-level AI draft (isDraft), an in-modal
      // paste-parse (parsedMeta), or a plain hand-typed entry — the first two keep their real
      // intake linkage + AI decision so the audit trail survives the parser's move into the modal.
      const meta = isDraft
        ? { rawText: rawText || `[client] ${party.name}`, decision: decision || 'new', confidence: confidence ?? 1, intakeId: intakeId || null }
        : parsedMeta
          ? { rawText: parsedMeta.rawText, decision: parsedMeta.decision, confidence: parsedMeta.confidence ?? 1, intakeId: parsedMeta.intakeId }
          : { rawText: `[manual entry via Data Desk] ${party.name}`, decision: 'manual', confidence: 1, intakeId: null }
      const resp = await commitIntakeEnvelope({
        raw_text: meta.rawText,
        channel: 'scheduler',
        envelope: { decision: meta.decision, confidence: meta.confidence, commands: [cmd] },
        anchors: null, intake_id: meta.intakeId,
        agent_id: agentId,
      })
      if ((resp.results || []).some(r => !r.executed)) {
        setErr(resp.results[0]?.result?.message || 'gateway refused')
      } else {
        // The commit response rides along (2026-07-29): callers that need the created
        // ids (MeetingModal picks the new party as the meeting's client) read
        // results[].result.{requirement_id,person_id}; existing callers ignore the arg.
        onSaved(resp); onClose()
      }
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <Modal title={presetParty ? tLog('desk_add_request_title', { name: presetParty.name || t('desk_person_unnamed') }) : isDraft ? t('desk_client_review_title') : t('desk_add_client')} onClose={onClose}>
      <div className="space-y-4">
        {/* Three ways in, top to bottom: send the client a link / AI parse a paste /
            fill the form yourself (2026-07-15 design). */}
        {!isDraft && (
          <FillLinkSection kind="client" agentId={agentId} agentName={agentName}
            getPrefill={() => ({ name: party.name, phone: party.phone, intent: reqInfo.intent, property_category: reqInfo.property_category,
              price_min: criteria.price_min, price_max: criteria.price_max, currency: criteria.currency,
              district_codes: criteria.district_codes, rooms_min: criteria.rooms_min, rooms_max: criteria.rooms_max, bedrooms_min: criteria.bedrooms_min })} />
        )}
        {!isDraft && (
          <div className={card}>
            <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_modal_paste_heading')}</p>
            <textarea className={`${input} font-mono text-sm`} rows={3} value={pasteText}
              placeholder={t('desk_modal_paste_client_ph')}
              onChange={e => setPasteText(e.target.value)} />
            <button className={`${btn('ghost')} mt-2`} disabled={parsing || !pasteText.trim()} onClick={handlePasteParse}>
              {parsing ? t('desk_modal_parsing') : t('desk_modal_parse_to_draft')}
            </button>
            {parsedMeta && <span className="ml-2 text-xs text-muted-foreground"><IconText>{t('desk_modal_parsed_check_fields')}</IconText></span>}
          </div>
        )}
        {/* One row per field this parse disagrees with. Same component as the property modal —
            a second panel would be a second set of rules about the same question. */}
        <ParseConflictPanel
          conflicts={parseConflicts}
          answers={parseAnswers}
          cardClass={card}
          onToggle={(key, next) => setParseAnswers(a => ({ ...a, [key]: next }))}
          onApply={() => {
            applyClientPatch(acceptedClientPatch(parseConflicts, parseAnswers, parseRaw, { party, reqInfo, criteria }))
            setParseConflicts([]); setParseAnswers({})
          }}
          onKeepAll={() => { setParseConflicts([]); setParseAnswers({}) }}
        />
        <div className={card}>
          <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_review_section_party')}</p>
          {presetParty ? (
            /* B48: person LOCKED — this is a NEW request for an EXISTING person; the
               reuse-verified party_id is already bound, so no name/phone edit, no picker. */
            <div className="flex items-center gap-2 text-sm">
              <span className="text-base"><Icon name="user-check" /></span>
              <span className="font-medium truncate">{presetParty.name || t('desk_person_unnamed')}</span>
              {presetParty.phone && <span className="text-xs text-muted-foreground"><Icon name="phone" /> {presetParty.phone}</span>}
              <span className="ml-auto text-xs text-emerald-600 dark:text-emerald-400"><Icon name="check" /> {t('desk_addreq_bound')}</span>
            </div>
          ) : (<>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-muted-foreground col-span-2">{t('desk_client_name_label')}
              <input className={input} value={party.name} onChange={e => setParty(p => ({ ...p, name: e.target.value }))} placeholder={t('desk_client_name_ph')} />
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_client_phone')}<input className={input} value={party.phone}
              onChange={e => { setParty(p => ({ ...p, phone: e.target.value })); setPickedPartyId(null) }}
              onBlur={checkPartyCandidates} /></label>
            <label className="text-xs text-muted-foreground">{t('desk_client_email')}<input className={input} value={party.email}
              onChange={e => { setParty(p => ({ ...p, email: e.target.value })); setPickedPartyId(null) }}
              onBlur={checkPartyCandidates} /></label>
            {phoneWarn && (
              <p className="col-span-2 text-xs text-amber-600 dark:text-amber-400">
                {phoneWarn.reason === 'lead'
                  ? t('desk_phone_shape_lead')
                  : tLog('desk_phone_shape_len', { cc: phoneWarn.cc ? phoneWarn.cc + ' ' : '', want: phoneWarn.want, got: phoneWarn.got })}
              </p>
            )}
          </div>
          {/* existing-party candidates (0123): pick = reuse; ignore = create new + merge hypothesis */}
          {pickedPartyId ? (
            <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
              <Icon name="check" /> {t('desk_party_using_existing')}{' '}
              <button className="underline text-muted-foreground" onClick={() => setPickedPartyId(null)}>{t('desk_party_create_new')}</button>
            </p>
          ) : partyCands.length > 0 && (
            <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-2">
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">{t('desk_party_existing_found')}</p>
              <div className="space-y-1">
                {partyCands.map(c => (
                  <div key={c.party_id} className="flex items-center justify-between text-sm gap-2">
                    <span className="truncate">
                      {c.display_name}
                      <span className="text-xs text-muted-foreground ml-1.5">
                        {c.primary_phone || c.primary_email}
                        {/* fragments, not template literals: an <Icon> inside `${}` would
                            stringify to [object Object] — the count has to leave the string
                            for the glyph to become an SVG (2026-07-20 sweep). */}
                        {' · '}{c.listings_count > 0 && <>{c.listings_count} <Icon name="house" /> </>}
                        {c.requirements_count > 0 && <>{c.requirements_count} <Icon name="search" /> </>}
                        {c.cooperations_count > 0 && <>{c.cooperations_count} <Icon name="handshake" /></>}
                      </span>
                    </span>
                    <button className="text-xs text-primary shrink-0" onClick={() => {
                      setPickedPartyId(c.party_id)
                      pickSnapRef.current = { phone: (party.phone.trim() || c.primary_phone || '').trim(),
                        email: (party.email.trim() || c.primary_email || '').trim() }
                      setParty(p => ({ ...p,
                        name: p.name.trim() || c.display_name || '',
                        phone: p.phone.trim() || c.primary_phone || '',
                        email: p.email.trim() || c.primary_email || '' }))
                    }}>{t('desk_party_use')}</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>)}
        </div>

        <div className={card}>
          <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_review_section_intent')}</p>
          <div className="grid grid-cols-3 gap-3">
            <label className="text-xs text-muted-foreground">{t('desk_client_looking_to')}
              {/* ⚠️ a budget belongs to its deal (form audit I21, 2026-10-12): $1,500 a month is not a $1,500 purchase. Changing the
                  deal empties the budget so it is typed again in the new meaning — the public form does the same. */}
              <select className={input} value={reqInfo.intent} onChange={e => { const v = e.target.value
                if (v !== reqInfo.intent) setCriteria(c => ({ ...c, price_min: undefined, price_max: undefined }))
                setReqInfo(r => ({ ...r, intent: v })) }}>
                {/* Normal options are buy/rent/short_term_rent. A stray current value (a
                    legacy or AI-parsed 'buy_or_rent') is shown ONLY when already set — so it
                    renders + the agent can correct it, but fresh clients can't newly pick it. */}
                {(INTENT_OPTIONS.includes(reqInfo.intent) ? INTENT_OPTIONS : [reqInfo.intent, ...INTENT_OPTIONS])
                  .map(o => <option key={o} value={o}>{intentLabel(o, 'demand')}</option>)}
              </select>
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_client_property_type')}
              <select className={input} value={reqInfo.property_category} onChange={e => setReqInfo(r => ({ ...r, property_category: e.target.value }))}>
                {PROPERTY_CATEGORY_OPTIONS.map(o => <option key={o} value={o}>{t(PROPERTY_CATEGORY_KEYS[o])}</option>)}
              </select>
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_client_urgency')}
              <select className={input} value={reqInfo.urgency} onChange={e => setReqInfo(r => ({ ...r, urgency: e.target.value }))}>
                <option value="">—</option>
                {URGENCY_OPTIONS.map(o => <option key={o} value={o}>{t(URGENCY_OPTION_KEYS[o])}</option>)}
              </select>
            </label>
          </div>
        </div>

        <CriteriaFieldsForm form={criteria} set={set} districts={districts} features={features} intent={reqInfo.intent} propertyCategory={reqInfo.property_category} />

        <BetterToKnow items={clientGaps(party, reqInfo, criteria).map(g => g.label)} />

        {err && <p className="text-sm text-destructive"><IconText>{tLog('desk_client_not_saved_err', { err })}</IconText></p>}

        <div className="flex justify-end gap-2 pt-2">
          <button className={btn('ghost')} onClick={onClose}>{t('desk_client_cancel')}</button>
          <button className={btn('primary')} disabled={saving || !canSave} onClick={handleSave}>
            {saving ? t('desk_review_saving') : isDraft ? t('desk_client_accept_save') : t('desk_client_add_button')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── One-time fill-form link (client/property self-intake — core 0095/0096) ────
// The first SECTION of both Add modals ("Send link / AI parse / Fill yourself"):
// the agent mints a token and sends /fill.html?t=<token> to the client; the
// client fills the PUBLIC wizard themselves and the entry lands on this agent's
// Desk (created_by_agent_id = link creator), exactly like a manual add. The link
// is one-time (consumed on submit) and expires in 7 days. kind is fixed by which
// modal hosts the section; prefill is read LIVE from the host form's name/phone
// (getPrefill) at create time, so typing the client's name first pre-fills their
// form. Server: lib/core_fill.js.
function FillLinkSection({ agentId, agentName, kind, getPrefill }) {
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState(null)   // { token, kind }
  const [links, setLinks] = useState(null)       // lazy — loaded when the list is expanded
  const [showLinks, setShowLinks] = useState(false)
  const [copied, setCopied] = useState(false)
  const [err, setErr] = useState(null)

  // ── THE PATH IS THE SHARE CARD (owner 2026-09-08) ──────────────────────────
  // "Can we add preview that will tell users it's fill client requirements or
  // fill property details?" A link-preview crawler runs no JavaScript and never
  // sends the ?t= token anywhere it could be resolved, so the card is whatever
  // the STATIC html says — which means the kind has to be in the path.
  // /client.html and /property.html are the same fill app with different <head>
  // tags; the token still decides the form, so these two can never disagree
  // with what actually opens. ⚠️ Anything not `client`/`property` falls back to
  // /fill.html, whose card is deliberately true of either — a new kind must get
  // a vague preview, never a confidently wrong one.
  // 'candidate' (2026-09-09) is the PERSONAL vacancy link — the same one-time token, sent to
  // one person the recruiter found, opening the same public form the job-board link opens. The
  // portal link is a different object entirely: permanent, no token, minted never. Do not add
  // it here; it is a constant you paste, not something anyone generates.
  // The wizard lives on sentavus.com now (owner 2026-09-21: "give that form new UI and improved UX"; public-site
  // components/fill/ is this repo's FillApp, same rules and endpoints). The kind stays in the PATH — sentavus.com
  // serves the same per-kind cards as client.html / property.html did. No locale prefix on purpose: the site picks
  // the recipient's language from their browser, which is what the old wizard's detectLang did. The old .html pages
  // redirect there (vercel.json), so links already sent keep working.
  const FILL_PATH = { client: 'fill/client', property: 'fill/property' }
  // a candidate's personal link opens the sentavus.com job page (owner 2026-09-19), which resolves the same ?t= token
  const urlFor = token => (kind === 'candidate'
    ? `https://sentavus.com/ka/jobs/real-estate-agent-tbilisi?t=${token}`
    : `https://sentavus.com/${FILL_PATH[kind] || 'fill'}/${token}`)
  // agentId is optional now (0156): without it the server lists the VERIFIED
  // viewer's own links — an admin's ride on created_by_party_id (their staff
  // party) plus any legacy auth-uid rows, so their list is no longer empty.
  const loadLinks = () => {
    listFillLinks(agentId).then(d => setLinks((d.links || []).filter(l => l.kind === kind))).catch(() => setLinks([]))
  }

  async function handleCreate() {
    setCreating(true); setErr(null); setCopied(false)
    try {
      const p = getPrefill ? getPrefill() : {}
      const r = await createFillLink({ kind, agent_id: agentId, agent_name: agentName,
        // the rest of what the agent typed rides along (I37, 2026-10-12): the server whitelists it, the form opens with it
        prefill: { ...p, name: (p.name || '').trim() || null, phone: (p.phone || '').trim() || null } })
      setCreated(r)
      if (showLinks) loadLinks()
    } catch (e) { setErr(e.message) }
    finally { setCreating(false) }
  }
  async function copy(token) {
    try { await navigator.clipboard.writeText(urlFor(token)); setCopied(true); setTimeout(() => setCopied(false), 1500) }
    catch { /* clipboard blocked — the url is visible to select manually */ }
  }
  const waLink = token => {
    const p = getPrefill ? getPrefill() : {}
    return `https://wa.me/${(p.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(
      tLog('desk_fill_link_share_text', { name: (p.name || '').trim() ? ` ${p.name.trim()}` : '', url: urlFor(token) }))}`
  }

  return (
    <div className={card}>
      <p className="text-xs font-semibold text-muted-foreground mb-1"><Icon name="link" /> {t(kind === 'candidate' ? 'cand_link_title' : 'desk_fill_link_title')}</p>
      <p className="text-xs text-muted-foreground mb-2">{t(kind === 'candidate' ? 'cand_link_sub' : 'desk_fill_link_sub')}</p>
      {!created ? (
        // No !agentId guard: an admin/manager login has no agents row (agentId null)
        // and must still be able to mint links — the server stores created_by_agent_id
        // null, so the resulting entry lands in the admin/manager feed (2026-07-16 fix:
        // the disabled button read as "not working" on the admin's phone).
        <button className={btn('ghost')} disabled={creating} onClick={handleCreate}>
          {creating ? t('desk_fill_link_generating') : t('desk_fill_link_generate')}
        </button>
      ) : (
        <div>
          <p className="break-all font-mono text-xs text-foreground">{urlFor(created.token)}</p>
          <div className="mt-2 flex gap-2">
            <button className={btn('primary')} onClick={() => copy(created.token)}>
              {copied ? t('desk_fill_link_copied') : <IconText>{`📋 ${t('desk_fill_link_copy')}`}</IconText>}
            </button>
            <a className={btn('ghost')} href={waLink(created.token)} target="_blank" rel="noreferrer">
              <Icon name="message-circle" /> {t('desk_fill_link_whatsapp')}
            </a>
          </div>
        </div>
      )}
      {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
      <button className="mt-2 block text-xs text-muted-foreground underline decoration-dotted"
        onClick={() => { setShowLinks(s => !s); if (!showLinks && links === null) loadLinks() }}>
        {t('desk_fill_link_recent')}
      </button>
      {showLinks && (
        links === null ? <p className="mt-1 text-xs text-muted-foreground">…</p>
        : !links.length ? <p className="mt-1 text-xs text-muted-foreground">{t('desk_fill_link_none')}</p>
        : (
          <div className="mt-1 max-h-40 space-y-1 overflow-y-auto">
            {links.map(l => (
              <div key={l.id} className="flex items-center gap-2 rounded-lg border border-border px-2 py-1.5 text-xs">
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  {/* partial capture (0098): what the client typed so far beats the prefill */}
                  {[l.draft_name || l.prefill?.name, l.draft_phone || l.prefill?.phone].filter(Boolean).join(' · ')
                    || new Date(l.created_at).toLocaleDateString()}
                </span>
                <span className={l.status === 'used' ? 'text-green-600 dark:text-green-400'
                  : l.status === 'expired' ? 'text-muted-foreground'
                  : l.status === 'started' ? 'text-sky-600 dark:text-sky-400' : 'text-amber-600 dark:text-amber-400'}>
                  {t(`desk_fill_link_st_${l.status}`)}
                </span>
                {(l.status === 'open' || l.status === 'started') && (
                  <button className="shrink-0 rounded border border-border px-1.5 py-0.5 hover:bg-accent"
                    onClick={() => copy(l.token)}><Icon name="clipboard" /></button>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}

// ── Exports — everything IntakeTab.jsx (or a sibling desk module) still references ──
export {
  extractRequirementCmd, amendRequirementCmd,
  INTENT_OPTIONS, PROPERTY_CATEGORY_OPTIONS, URGENCY_OPTIONS,
  PROPERTY_CATEGORY_KEYS, URGENCY_OPTION_KEYS,
  PolygonMapPicker, TierTagPicker,
  APT_CLASS, PROP_STATE, PROJECT_TYPES, TYPE_CHIP_CODES, PROP_TYPE_TAGS,
  featureDefOf, InfoDot, FeatureDefPanel, isNationalityFeature,
  CriteriaFieldsForm, ManualClientModal, FillLinkSection,
}
